import { Box, Divider, List, ListItemButton, ListItemText, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FC, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WebAppState } from 'web/state/store.web';
import { FormTitle } from './form-title';
import { localized } from 'shared/state/i18n/i18n';
import { GetUserSitesForCurrentUserAsync } from 'shared/state/ducks/user-sites/operations';
import { useEditContext } from './create-site-context-provider';

interface Props {}

export const SiteList: FC<Props> = memo(() => {
	const dispatch = useDispatch();
	const userSites = useSelector((state: WebAppState) => state.userSites.userSites);
	const { editingSiteId, isLoadingEdit, startEditing } = useEditContext();
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		if (!userSites || userSites.length === 0) {
			dispatch(GetUserSitesForCurrentUserAsync() as any);
		}
	}, [dispatch, userSites]);

	const filteredSites = useMemo(
		() => userSites.filter(site => site.siteName?.toLowerCase().includes(debouncedSearch.toLowerCase())),
		[userSites, debouncedSearch],
	);

	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	}, []);

	const handleSelect = useCallback(
		(siteId: string | undefined) => {
			if (!siteId) return;
			startEditing(siteId);
		},
		[startEditing],
	);

	return (
		<Box className="site-list-container">
			<FormTitle title={localized('Farms')} />
			<TextField
				size="small"
				placeholder={localized('Search')}
				value={search}
				onChange={handleSearchChange}
				className="site-list-search"
			/>
			<List disablePadding={true}>
				{filteredSites.map((site, index) => (
					<React.Fragment key={site.siteId ?? index}>
						<ListItemButton
							selected={site.siteId === editingSiteId}
							disabled={isLoadingEdit}
							onClick={() => handleSelect(site.siteId)}
							className="site-list-item"
						>
							<ListItemText primary={site.siteName} />
						</ListItemButton>
						{index < filteredSites.length - 1 && <Divider component="li" />}
					</React.Fragment>
				))}
			</List>
		</Box>
	);
});
